import {
  Users,
  Users2,
  MapPin,
  UserCog,
  Building2,
  Plug,
  ShieldCheck,
  Tags,
  Building,
  GitBranch,
  CreditCard,
  Workflow,
  CalendarClock,
} from 'lucide-react';
import { P, type PermissionString } from '@vagusx/shared';

export interface SetupNavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  /**
   * Permission required to open this setup page — both the nav item and the
   * route guard (`requireSetupPageAccess`) read this field, so it is the
   * single source of truth.
   *
   * Setup is org-level config, so pages gate on the *manage/edit*
   * permission, not view: operational roles (e.g. Staff) hold `*_view`
   * grants for dropdowns, schedulers, and pickers, and those must not
   * grant access to config screens. The exceptions are pages whose content
   * exists only in Setup (Roles, Integrations) — there, holding the view
   * permission is itself the grant. Personal pages (passkeys) live under
   * /profile.
   */
  permission: PermissionString;
}

export const setupNavItems: SetupNavItem[] = [
  {
    title: 'Organization',
    url: '/setup/organization',
    icon: Building2,
    description: 'Organization settings',
    permission: P.ORGANIZATIONS_EDIT,
  },
  {
    title: 'Providers',
    url: '/setup/providers',
    icon: UserCog,
    description: 'Manage healthcare providers',
    permission: P.PROVIDERS_EDIT,
  },
  {
    title: 'Locations',
    url: '/setup/locations',
    icon: MapPin,
    description: 'Manage practice locations',
    permission: P.LOCATIONS_EDIT,
  },
  {
    title: 'Appointment Types',
    url: '/setup/appointment-types',
    icon: CalendarClock,
    description: 'Types, schedule blocks, and scheduling notes',
    permission: P.APPOINTMENTS_EDIT,
  },
  {
    title: 'Users',
    url: '/setup/users',
    icon: Users,
    description: 'Manage staff and user accounts',
    permission: P.USERS_EDIT,
  },
  {
    title: 'Roles',
    url: '/setup/roles',
    icon: ShieldCheck,
    description: 'Manage roles and permissions',
    permission: P.ROLES_VIEW,
  },
  {
    title: 'Teams',
    url: '/setup/teams',
    icon: Users2,
    description: 'Group users and agents for shared task assignment',
    permission: P.TEAMS_EDIT,
  },
  {
    title: 'Labels',
    url: '/setup/labels',
    icon: Tags,
    description: 'Document classification labels',
    permission: P.CLASSIFICATION_LABELS_EDIT,
  },
  {
    title: 'Payors',
    url: '/setup/payors',
    icon: CreditCard,
    description: 'Insurance payors and accepted plans',
    permission: P.PAYORS_EDIT,
  },
  {
    title: 'Integrations',
    url: '/setup/integrations',
    icon: Plug,
    description: 'EHR integrations and sync',
    permission: P.INTEGRATIONS_VIEW,
  },
  {
    title: 'Entities',
    url: '/setup/entities',
    icon: Building,
    description: 'External providers, labs, pharmacies',
    permission: P.ENTITIES_EDIT,
  },
  {
    title: 'Assignment Rules',
    url: '/setup/assignment-rules',
    icon: GitBranch,
    description: 'Auto-assign tasks by conditions',
    permission: P.ASSIGNMENT_RULES_EDIT,
  },
  {
    title: 'Workflows',
    url: '/setup/workflows',
    icon: Workflow,
    description: 'What the agent says on a call, and when each step runs',
    permission: P.AGENTS_EDIT,
  },
];

/**
 * Setup nav items the user may open. Pass an empty set / `false` while
 * permissions are loading — items stay hidden instead of flashing.
 */
export function visibleSetupItems(
  permissions: ReadonlySet<string>,
  isSystemAdmin: boolean
): SetupNavItem[] {
  if (isSystemAdmin) return setupNavItems;
  return setupNavItems.filter((item) => permissions.has(item.permission));
}

/**
 * Whether the user can access any setup page. Drives the Setup entry in
 * the app sidebar.
 */
export function hasAnySetupAccess(
  permissions: ReadonlySet<string>,
  isSystemAdmin: boolean
): boolean {
  return visibleSetupItems(permissions, isSystemAdmin).length > 0;
}

/**
 * First setup page the user can open, in nav order, or null when the user
 * has no setup access at all.
 */
export function firstAccessibleSetupUrl(
  permissions: ReadonlySet<string>,
  isSystemAdmin: boolean
): string | null {
  return visibleSetupItems(permissions, isSystemAdmin)[0]?.url ?? null;
}
